import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from './api';
import { BrandHeader } from './Brand';
import { dayKey } from './pnl';
import AdminAnalytics from './AdminAnalytics';
import { AdminBriefing, ProblemsPanel } from './AdminBriefing';
import AdminBackups from './AdminBackups';
import { confirmDialog } from './confirmDialog';
import MarkCosts from './MarkCosts';
import { FeatureSwitches, FunnelPanel, HealthPanel, MemberDetail, MemberLink, MoneyPanel, ProLaunchPanel, QuietPanel } from './AdminTools';
import { alertsEnabled, alertsOn, alertsSupported, announce, askForAlerts, ding, setAlertsEnabled, useUnreadMessages } from './adminAlerts';
import { currentSubscription, pushSupported, turnOffPush, turnOnPush } from './pushClient';

const CHART_DAYS = 30;
const CHART_HEIGHT = 160;

function formatDate(date) {
  return new Date(date).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

function timeAgo(date) {
  const minutes = Math.round((Date.now() - new Date(date).getTime()) / 60000);
  if (minutes < 60) return minutes <= 1 ? 'just now' : `${minutes}m ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.round(hours / 24);
  if (days < 30) return `${days}d ago`;
  return formatDate(date);
}

// One entry per day for the last CHART_DAYS days, oldest first.
function signupsByDay(members) {
  const counts = {};
  for (const m of members) {
    const key = dayKey(m.joinedAt);
    counts[key] = (counts[key] || 0) + 1;
  }
  const days = [];
  for (let i = CHART_DAYS - 1; i >= 0; i--) {
    const date = new Date();
    date.setHours(12, 0, 0, 0);
    date.setDate(date.getDate() - i);
    const key = dayKey(date);
    days.push({ key, date, count: counts[key] || 0 });
  }
  return days;
}

// Bar with 4px rounded top corners, anchored to the baseline.
function barPath(x, y, w, h) {
  const r = Math.min(4, w / 2, h);
  return `M${x},${y + h}V${y + r}Q${x},${y} ${x + r},${y}H${x + w - r}Q${x + w},${y} ${x + w},${y + r}V${y + h}Z`;
}

function SignupChart({ members }) {
  const [hovered, setHovered] = useState(null);
  const [showTable, setShowTable] = useState(false);
  const days = signupsByDay(members);
  const total = days.reduce((sum, d) => sum + d.count, 0);
  const max = Math.max(1, ...days.map((d) => d.count));
  const width = 600;
  const slot = width / days.length;
  const barWidth = slot - 2;
  const label = (d) => d.date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });

  return (
    <section className="admin-panel">
      <div className="admin-panel-header">
        <div>
          <h2>Sign-ups per day</h2>
          <p className="admin-panel-sub">Last {CHART_DAYS} days &bull; {total} total</p>
        </div>
        <button type="button" className="secondary small" onClick={() => setShowTable(!showTable)}>
          {showTable ? 'Show chart' : 'Show table'}
        </button>
      </div>

      {showTable ? (
        <div className="table-wrap admin-chart-table">
          <table>
            <thead>
              <tr><th>Day</th><th>Sign-ups</th></tr>
            </thead>
            <tbody>
              {[...days].reverse().map((d) => (
                <tr key={d.key}><td>{label(d)}</td><td>{d.count}</td></tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="admin-chart" onMouseLeave={() => setHovered(null)}>
          <div className="admin-chart-ylabels">
            <span>{max}</span>
            <span>0</span>
          </div>
          <div className="admin-chart-plot">
            <svg viewBox={`0 0 ${width} ${CHART_HEIGHT}`} preserveAspectRatio="none" role="img"
              aria-label={`Sign-ups per day for the last ${CHART_DAYS} days, ${total} in total`}>
              <line x1="0" x2={width} y1={CHART_HEIGHT / 2} y2={CHART_HEIGHT / 2} className="admin-chart-grid" />
              {days.map((d, i) => {
                const h = (d.count / max) * (CHART_HEIGHT - 4);
                return (
                  <g key={d.key}>
                    {d.count > 0 && (
                      <path d={barPath(i * slot + 1, CHART_HEIGHT - h, barWidth, h)}
                        className={`admin-chart-bar${hovered === i ? ' hovered' : ''}`} />
                    )}
                    {/* Full-height hit target so empty and short days are easy to hover */}
                    <rect x={i * slot} y="0" width={slot} height={CHART_HEIGHT} fill="transparent"
                      onMouseEnter={() => setHovered(i)} />
                  </g>
                );
              })}
              <line x1="0" x2={width} y1={CHART_HEIGHT - 0.5} y2={CHART_HEIGHT - 0.5} className="admin-chart-axis" />
            </svg>
            {hovered !== null && (
              <div
                className={`admin-chart-tooltip${hovered > days.length * 0.85 ? ' align-right' : ''}${hovered < days.length * 0.15 ? ' align-left' : ''}`}
                style={{ left: `${((hovered + 0.5) / days.length) * 100}%` }}
              >
                <div><strong>{days[hovered].count}</strong> sign-up{days[hovered].count === 1 ? '' : 's'}</div>
                <span>{label(days[hovered])}</span>
              </div>
            )}
            <div className="admin-chart-xlabels">
              <span>{label(days[0])}</span>
              <span>{label(days[Math.floor(days.length / 2)])}</span>
              <span>Today</span>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}

// Turns new-message alerts (the ding, plus desktop pop-ups if the browser allows them) on or off.
// The first time they're turned on, the browser asks to allow pop-ups.
function AlertsButton() {
  const [on, setOn] = useState(alertsEnabled());
  async function toggle() {
    if (on) {
      setAlertsEnabled(false);
      setOn(false);
      return;
    }
    setAlertsEnabled(true);
    setOn(true);
    if (alertsSupported() && !alertsOn() && window.Notification.permission === 'default') await askForAlerts();
    ding();
  }
  const blocked = on && alertsSupported() && window.Notification.permission === 'denied';
  return (
    <button type="button" className={`secondary small${on ? ' active' : ''}`} onClick={toggle} aria-pressed={on}
      title={on ? (blocked ? 'Sound on. Pop-ups are blocked in your browser settings.' : 'Click to turn sound and pop-ups off') : 'Click to turn sound and pop-ups on'}>
      {on ? '🔔 Alerts on' : '🔕 Alerts off'}
    </button>
  );
}

// Push notifications for admin alerts on this device: new messages and chats, new members and
// problems, even when the admin page is closed (push.js). Turned off per kind in Settings.
function PushButton() {
  const [on, setOn] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (pushSupported()) currentSubscription().then((s) => setOn(Boolean(s))).catch(() => setOn(false)); }, []);
  if (!pushSupported() || on === null) return null;
  async function toggle() {
    setBusy(true);
    try {
      if (on) { await turnOffPush(); setOn(false); } else {
        const r = await turnOnPush();
        if (r === 'on') setOn(true);
        else window.alert(r === 'denied' ? 'Notifications are blocked in your browser settings for this site.' : 'This browser does not support push notifications.');
      }
    } finally { setBusy(false); }
  }
  return (
    <button type="button" className={`secondary small${on ? ' active' : ''}`} onClick={toggle} disabled={busy} aria-pressed={on}
      title={on ? 'Push notifications reach this device even when Market Matrix is closed. Click to turn them off here.' : 'Get admin alerts on this device even when Market Matrix is closed'}>
      {on ? '📲 Push on' : '📲 Push off'}
    </button>
  );
}

const SIGNATURE = '\n\n\n\nLou\nMarket Matrix\nmarketmatrixhq.com';

// New email from hello@marketmatrixhq.com: saved as a conversation, so the answer lands in it.
function NewEmail({ onSent, onClose }) {
  const [form, setForm] = useState({ to: '', name: '', subject: '', text: `Hello,${SIGNATURE}` });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));
  const greeting = (name) => `${name.trim() ? `Hi ${name.trim().split(' ')[0]},` : 'Hello,'}`;

  // The greeting follows the name typed, until the message itself is edited.
  function setName(e) {
    const name = e.target.value;
    setForm((f) => (f.text.startsWith(greeting(f.name)) && f.text.slice(greeting(f.name).length) === SIGNATURE
      ? { ...f, name, text: `${greeting(name)}${SIGNATURE}` } : { ...f, name }));
  }

  async function send(e) {
    e.preventDefault();
    setBusy(true);
    setError('');
    try {
      onSent(await api('/admin/messages/new', { method: 'POST', body: form }));
    } catch (err) {
      setError(err.message);
      setBusy(false);
    }
  }

  return (
    <form className="inbox-reply inbox-new" onSubmit={send}>
      <span className="inbox-reply-from">New email from hello@marketmatrixhq.com</span>
      <div className="inbox-new-row">
        <label>To<input type="email" value={form.to} onChange={set('to')} placeholder="name@example.com" required autoFocus /></label>
        <label>Their name (optional)<input value={form.name} onChange={setName} placeholder="For the greeting" /></label>
      </div>
      <label>Subject<input value={form.subject} onChange={set('subject')} maxLength={300} required /></label>
      <label>Message<textarea rows="10" value={form.text} onChange={set('text')} required /></label>
      {error && <p className="error">{error}</p>}
      <div className="inbox-actions">
        <button type="submit" className="small" disabled={busy || !form.to.trim() || !form.subject.trim() || !form.text.trim()}>{busy ? 'Sending…' : 'Send email'}</button>
        <button type="button" className="secondary small" disabled={busy} onClick={onClose}>Cancel</button>
        <span className="field-hint">Their answer will show up here in your Inbox.</span>
      </div>
    </form>
  );
}

// The admin inbox: contact-form messages and emails to hello@, with the conversation
// that followed. Replies and new emails go out from hello@marketmatrixhq.com (inbox.js on the server).
function Inbox({ messages, onUpdate, onReplace, onDelete, onAdd }) {
  const [composing, setComposing] = useState(false);
  const [sentNote, setSentNote] = useState('');
  const [filter, setFilter] = useState('new');
  const [busy, setBusy] = useState(null);
  const [error, setError] = useState('');
  const [replying, setReplying] = useState(null);
  const [draft, setDraft] = useState('');
  const newCount = messages.filter((m) => m.status === 'new').length;
  const shown = filter === 'new' ? messages.filter((m) => m.status === 'new') : messages;

  async function run(message, fn) {
    setBusy(message._id);
    setError('');
    try {
      await fn();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(null);
    }
  }

  const setStatus = (m, status) => run(m, async () => {
    await api(`/admin/messages/${m._id}`, { method: 'PATCH', body: { status } });
    onUpdate(m._id, status);
  });

  const sendReply = (m) => run(m, async () => {
    const updated = await api(`/admin/messages/${m._id}/reply`, { method: 'POST', body: { text: draft } });
    onReplace(updated);
    setReplying(null);
    setDraft('');
  });

  // A file sent with "Add my platform": a short-lived link from storage, or the file itself.
  async function downloadFile(m) {
    try {
      const res = await fetch(`/api/admin/messages/${m._id}/file`);
      if (!res.ok) throw new Error((await res.json().catch(() => ({}))).message || 'Could not download the file');
      if ((res.headers.get('content-type') || '').includes('application/json')) {
        window.location.assign((await res.json()).url);
        return;
      }
      const a = document.createElement('a');
      a.href = URL.createObjectURL(await res.blob());
      a.download = m.attachment.name;
      a.click();
    } catch (err) {
      setError(err.message);
    }
  }

  async function removeFile(m) {
    const ok = await confirmDialog({ title: 'Delete this file?', message: `${m.attachment.name} will be deleted. The message stays.`, confirmLabel: 'Delete file', danger: true });
    if (!ok) return;
    run(m, async () => {
      await api(`/admin/messages/${m._id}/file`, { method: 'DELETE' });
      onReplace({ ...m, attachment: undefined });
    });
  }

  async function remove(m) {
    const ok = await confirmDialog({ title: 'Delete this message?', message: `The message from ${m.name} and its conversation will be removed from your Inbox. This can't be undone.`,
      confirmLabel: 'Delete', danger: true });
    if (!ok) return;
    run(m, async () => {
      await api(`/admin/messages/${m._id}`, { method: 'DELETE' });
      onDelete(m._id);
    });
  }

  function openReply(m) {
    setReplying(m._id);
    setDraft(`${m.name.includes('@') ? 'Hello,' : `Hi ${m.name.split(' ')[0]},`}\n\n\n\nLou\nMarket Matrix`);
  }

  return (
    <section className="admin-panel" id="admin-inbox">
      <div className="admin-panel-header">
        <div>
          <h2>Inbox</h2>
          <p className="admin-panel-sub">
            Contact form and emails to hello@marketmatrixhq.com &bull; {newCount} new
          </p>
        </div>
        <div className="inbox-filter">
          <button type="button" className="small" onClick={() => { setComposing(true); setSentNote(''); }} disabled={composing}>✉️ New email</button>
          <AlertsButton />
          <PushButton />
          <button type="button" className={`secondary small${filter === 'new' ? ' active' : ''}`} onClick={() => setFilter('new')}>
            New
          </button>
          <button type="button" className={`secondary small${filter === 'all' ? ' active' : ''}`} onClick={() => setFilter('all')}>
            All
          </button>
        </div>
      </div>
      {composing && <NewEmail onClose={() => setComposing(false)} onSent={(m) => { onAdd(m); setComposing(false); setFilter('all'); setSentNote(`Sent to ${m.email}.`); }} />}
      {sentNote && <p className="success">{sentNote}</p>}
      {error && <p className="error">{error}</p>}
      {shown.length === 0 ? (
        <p className="empty">{filter === 'new' ? 'No new messages.' : 'No messages yet.'}</p>
      ) : (
        <ul className="inbox-list">
          {shown.map((m) => (
            <li key={m._id} className={`inbox-item${m.status === 'done' ? ' done' : ''}`}>
              <div className="inbox-meta">
                <span className="inbox-category">{m.sentByUs ? 'Sent' : m.source === 'email' ? 'Email' : m.category}</span>
                {m.sentByUs && <span className="inbox-email">To</span>}
                <strong>{m.name}</strong>
                <span className="inbox-email">{m.email}</span>
                {!m.userId && <span className="inbox-tag">{m.source === 'email' ? 'Not a member' : 'Visitor'}</span>}
                <span className="inbox-date">{timeAgo(m.createdAt)}</span>
              </div>
              {m.source === 'email' && m.subject && <p className="inbox-subject">{m.subject}</p>}
              {m.sentByUs ? (
                <div className="inbox-thread inbox-thread-out">
                  <span className="inbox-thread-who">You wrote &middot; {timeAgo(m.createdAt)}</span>
                  <p className="inbox-message">{m.message}</p>
                </div>
              ) : <p className="inbox-message">{m.message}</p>}
              {m.attachment && m.attachment.name && (
                <p className="inbox-file">
                  📎 <strong>{m.attachment.name}</strong> <span>({Math.max(1, Math.round((m.attachment.size || 0) / 1024))} KB)</span>
                  <button type="button" className="link-button" onClick={() => downloadFile(m)}>Download</button>
                  <button type="button" className="link-button danger-link" onClick={() => removeFile(m)}>Delete file</button>
                </p>
              )}
              {(m.thread || []).map((x, i) => (
                // eslint-disable-next-line react/no-array-index-key
                <div key={i} className={`inbox-thread inbox-thread-${x.direction}`}>
                  <span className="inbox-thread-who">{x.direction === 'out' ? 'You replied' : `${m.name.split(' ')[0]} wrote back`} &middot; {timeAgo(x.at)}</span>
                  <p className="inbox-message">{x.text}</p>
                </div>
              ))}
              {replying === m._id ? (
                <div className="inbox-reply">
                  <span className="inbox-reply-from">From hello@marketmatrixhq.com to {m.email}</span>
                  <textarea rows="7" value={draft} onChange={(e) => setDraft(e.target.value)} aria-label={`Reply to ${m.name}`} autoFocus />
                  <div className="inbox-actions">
                    <button type="button" className="small" disabled={busy === m._id || !draft.trim()} onClick={() => sendReply(m)}>
                      {busy === m._id ? 'Sending…' : 'Send reply'}
                    </button>
                    <button type="button" className="secondary small" disabled={busy === m._id} onClick={() => setReplying(null)}>Cancel</button>
                  </div>
                </div>
              ) : (
                <div className="inbox-actions">
                  <button type="button" className="small" onClick={() => openReply(m)}>Reply</button>
                  <button type="button" className="secondary small" disabled={busy === m._id}
                    onClick={() => setStatus(m, m.status === 'new' ? 'done' : 'new')}>
                    {m.status === 'new' ? 'Mark done' : 'Mark as new'}
                  </button>
                  <button type="button" className="secondary small inbox-delete" disabled={busy === m._id} onClick={() => remove(m)}>Delete</button>
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function AdminPage({ onLogout, onSessionExpired }) {
  const [stats, setStats] = useState(null);
  const [messages, setMessages] = useState([]);
  const [error, setError] = useState('');
  // Until two-step login is on, the admin page reminds you to turn it on.
  const [twoStepOn, setTwoStepOn] = useState(true);
  useEffect(() => { api('/auth/2fa/status').then((st) => setTwoStepOn(st.enabled)).catch(() => {}); }, []);

  useEffect(() => {
    Promise.all([api('/admin/stats'), api('/admin/messages')])
      .then(([s, m]) => {
        setStats(s);
        setMessages(m);
      })
      .catch((err) => {
        if (err.status === 401) onSessionExpired();
        else setError(err.message);
      });
  }, [onSessionExpired]);

  // New messages appear without a refresh, with a ding (and a desktop notification if allowed).
  const [briefKey, setBriefKey] = useState(0);
  useUnreadMessages(true, (count, latest) => {
    api('/admin/messages').then(setMessages).catch(() => {});
    setBriefKey((k) => k + 1);
    announce(count, latest);
  });
  const unread = messages.filter((m) => m.status === 'new').length;
  useEffect(() => {
    const before = document.title;
    document.title = `${unread ? `(${unread}) ` : ''}Admin · Market Matrix`;
    return () => { document.title = before; };
  }, [unread]);

  function updateMessage(id, status) {
    setMessages((list) => list.map((m) => (m._id === id ? { ...m, status } : m)));
  }

  function replaceMessage(updated) {
    setMessages((list) => list.map((m) => (m._id === updated._id ? updated : m)));
  }

  function deleteMessage(id) {
    setMessages((list) => list.filter((m) => m._id !== id));
  }

  const t = stats && stats.totals;
  const tiles = t && [
    { label: 'Members', value: t.members },
    { label: 'New this week', value: t.newThisWeek },
    { label: 'New this month', value: t.newThisMonth },
    {
      label: 'Uploaded trades',
      value: t.withTrades,
      note: t.members ? `${Math.round((t.withTrades / t.members) * 100)}% of members` : '',
    },
    { label: 'Active this week', value: t.activeThisWeek },
    { label: 'New messages', value: messages.filter((m) => m.status === 'new').length },
  ];

  return (
    <div className="App admin-wide">
      <BrandHeader>
        <Link to="/app" className="header-link">Journal</Link>
        <button type="button" className="secondary small" onClick={onLogout}>
          Log out
        </button>
      </BrandHeader>

      <div className="page-title">
        <p className="eyebrow">Admin</p>
        <h1>Members</h1>
        <p className="subtitle">Who's signing up and who's using Market Matrix. Only admins can see this page.</p>
      </div>

      {error && <p className="error">{error}</p>}
      {!stats && !error && <p className="empty">Loading…</p>}

      {!twoStepOn && (
        <p className="admin-twostep">
          🔐 <strong>Protect the admin page:</strong> turn on two-step login, so your password alone can't open it.{' '}
          <Link to="/app/settings#two-step">Turn it on in Settings &rarr;</Link>
        </p>
      )}

      {stats && (
        <>
          <AdminBriefing refreshKey={briefKey} />

          <section className="summary admin-summary">
            {tiles.map((tile) => (
              <div key={tile.label} className="stat">
                <span className="stat-label">{tile.label}</span>
                <span className="stat-value">{tile.value}</span>
                {tile.note && <span className="stat-note">{tile.note}</span>}
              </div>
            ))}
          </section>

          <HealthPanel />

          <Inbox messages={messages} onUpdate={updateMessage} onReplace={replaceMessage} onDelete={deleteMessage} onAdd={(m) => setMessages((list) => [m, ...list])} />

          <ProblemsPanel />

          <FunnelPanel />

          <QuietPanel />

          <ProLaunchPanel />
          <MoneyPanel />

          <FeatureSwitches />

          <AdminBackups />

          <MarkCosts />

          <AdminAnalytics />

          <SignupChart members={stats.members} />

          <section className="trades">
            <h2>All members</h2>
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Email</th>
                    <th>Joined</th>
                    <th>Last active</th>
                    <th>Trades</th>
                    <th>Notes</th>
                  </tr>
                </thead>
                <tbody>
                  {stats.members.map((m) => (
                    <tr key={m.id}>
                      <td className="symbol"><MemberLink id={m.id}>{m.name}</MemberLink></td>
                      <td className="admin-email">{m.email}</td>
                      <td>{formatDate(m.joinedAt)}</td>
                      <td>{timeAgo(m.lastActiveAt)}</td>
                      <td>{m.trades}</td>
                      <td>{m.notes}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        </>
      )}

      <MemberDetail />

      <footer className="footer">Market Matrix &bull; Admin</footer>
    </div>
  );
}

export default AdminPage;
